import { Button } from '@/components/ui/button'
import { useToast } from '@/components/ui/use-toast'
import { useEstateMediaTypes } from '@/estate/hooks/useEstateMediaTypes'
import { useEstateMedias } from '@/estateMedia/hooks/useEstateMedias'
import {
  Estate_Media_Type_Group_Enum,
  Estate_Media_Type_Name_Enum,
  useReorderEstateMediasMutation,
} from '@gql'
import { ImageUp } from 'lucide-react'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EstateMedia } from '../EstateMedia/EstateMedia'
import { EstateMediaMultipleFileUploadDialog } from '../EstateMedia/EstateMediaMultipleFileUploadDialog'

export type EstateMediasPhotosTabProps = {
  estateId: string
  readonly: boolean
}

export const EstateMediasPhotosTab = ({
  estateId,
  readonly,
}: EstateMediasPhotosTabProps) => {
  const { t } = useTranslation()
  const { toast } = useToast()

  const { estateMedias: estateMediasMedia } = useEstateMedias({
    estateId: estateId,
    typeGroup: Estate_Media_Type_Group_Enum.Media,
  })

  const estateMedias = useMemo(() => {
    return estateMediasMedia.filter(
      (em) => em.type.name === Estate_Media_Type_Name_Enum.Photo
    )
  }, [estateMediasMedia])

  const { estateMediaTypes } = useEstateMediaTypes({
    group: Estate_Media_Type_Group_Enum.Media,
  })
  const estateMediaTypePhoto = useMemo(() => {
    if (!estateMediaTypes) return undefined
    return estateMediaTypes.find(
      (type) => type.name === Estate_Media_Type_Name_Enum.Photo
    )
  }, [estateMediaTypes])

  const [reorderEstateMedias, { loading: reorderEstateMediasLoading }] =
    useReorderEstateMediasMutation({
      onError: (error) => {
        console.error(error)
        toast({
          title: t('useUpdateEstateMedia.error.title'),
          description: t('useUpdateEstateMedia.error.description'),
          variant: 'destructive',
        })
      },
      refetchQueries: ['getEstateMedia'],
    })

  const [isNewMediaOpen, setIsNewMediaOpen] = useState(false)

  const handleFileUpload = () => {
    setIsNewMediaOpen(false)
  }

  const handleMovePhoto = async (
    currentIndex: number,
    direction: 'up' | 'down'
  ) => {
    if (readonly || reorderEstateMediasLoading) return
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1
    if (targetIndex < 0 || targetIndex >= estateMedias.length) return

    const sourceMedia = estateMedias[currentIndex]
    const targetMedia = estateMedias[targetIndex]
    if (!sourceMedia || !targetMedia) return

    const sourcePosition = sourceMedia.position ?? currentIndex
    const targetPosition = targetMedia.position ?? targetIndex

    await reorderEstateMedias({
      variables: {
        updates: [
          {
            where: { id: { _eq: sourceMedia.id } },
            _set: { position: targetPosition },
          },
          {
            where: { id: { _eq: targetMedia.id } },
            _set: { position: sourcePosition },
          },
        ],
      },
    })
  }

  return (
    <>
      <h2 className="tw-text-2xl tw-font-semibold tw-mb-5">
        {t('EstateMediasPhotosTab.title')}
      </h2>
      <div className="tw-flex tw-flex-wrap tw-gap-4 tw-w-[650px]">
        {estateMedias.map((estateMedia, index) => {
          const isFirstMedia = index === 0
          const isLastMedia = index === estateMedias.length - 1

          return (
            <EstateMedia
              key={estateMedia.id}
              estateId={estateId}
              estateMedia={estateMedia}
              variant="photo"
              readonly={readonly}
              onMoveUp={() => void handleMovePhoto(index, 'up')}
              onMoveDown={() => void handleMovePhoto(index, 'down')}
              disableMoveUp={isFirstMedia || reorderEstateMediasLoading}
              disableMoveDown={isLastMedia || reorderEstateMediasLoading}
              moveUpAriaLabel={t('EstateMediasPhotosTab.moveUp')}
              moveDownAriaLabel={t('EstateMediasPhotosTab.moveDown')}
            />
          )
        })}
        {!readonly && (
          <Button
            variant="secondary"
            onClick={() => setIsNewMediaOpen(true)}
            className="tw-flex tw-flex-col tw-gap-4 tw-items-center tw-justify-center tw-w-[180px] tw-h-[180px] tw-bg-gray-100 tw-rounded-lg"
          >
            <ImageUp />
            {t('EstateMediasPhotosTab.add')}
          </Button>
        )}
      </div>
      <EstateMediaMultipleFileUploadDialog
        estateId={estateId}
        showToasts={true}
        accept="image/*"
        open={isNewMediaOpen}
        onOpenChange={() => setIsNewMediaOpen(false)}
        type={estateMediaTypePhoto!}
        onUpload={handleFileUpload}
      />
    </>
  )
}
